import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shareOrCopy } from "@/lib/utils";
import { toast } from "sonner";
import { BadgeCheck, Building2, Radio, Sparkles, BarChart3, Handshake, CheckCircle2, ImagePlus, Copy, Share2 } from "lucide-react";

const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

const ACTIVITIES = [
  { key: "vide_dressing", label: "Vide-dressing régulier" },
  { key: "friperie", label: "Friperie / revendeur" },
  { key: "createur", label: "Créateur / upcycling" },
  { key: "autre", label: "Autre" },
];
const VOLUMES = [
  { key: "moins_500", label: "Moins de 500 € / mois" },
  { key: "500_2000", label: "500 à 2 000 € / mois" },
  { key: "plus_2000", label: "Plus de 2 000 € / mois" },
];

const PERKS = [
  { icon: Building2, title: "Création d'entreprise offerte", desc: "Nous créons gratuitement votre micro-entreprise, en collaboration avec un cabinet formaliste partenaire. Zéro paperasse, zéro frais." },
  { icon: BadgeCheck, title: "Badge PRO ✓", desc: "Un badge de confiance sur votre profil et vos annonces qui rassure les acheteurs et booste vos ventes." },
  { icon: Radio, title: "Brocante Live", desc: "Vendez en direct vidéo devant votre communauté, avec enchères et paiement intégrés." },
  { icon: Sparkles, title: "Studio photo IA", desc: "Des photos dignes d'un shooting pro en un clic : fond studio et lumière corrigée, gratuitement." },
  { icon: BarChart3, title: "Statistiques hebdo", desc: "Chaque dimanche, votre récap : vues, favoris, ventes et gains de la semaine." },
  { icon: Handshake, title: "Accompagnement dédié", desc: "Un contact privilégié avec l'équipe Fripri pour développer votre activité." },
];

function ShopEditor({ user }) {
  const [form, setForm] = useState({
    shop_banner: user?.shop_banner || "",
    shop_headline: user?.shop_headline || "",
    shop_description: user?.shop_description || "",
    shop_slug: user?.shop_slug || slugify(user?.name),
  });
  const [savedSlug, setSavedSlug] = useState(user?.shop_slug || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd);
      setForm(f => ({ ...f, shop_banner: data.path }));
      toast.success("Bannière uploadée — pense à enregistrer !");
    } catch { toast.error("Échec de l'upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      setSavedSlug(form.shop_slug);
      toast.success("Vitrine mise à jour ✨");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const shopUrl = savedSlug ? `${window.location.origin}/boutique/${savedSlug}` : "";
  const richShareUrl = savedSlug ? `${API}/s/${savedSlug}` : "";
  const shareText = `Découvre ma boutique ${user?.name} sur Fripri 💚`;

  const copyLink = async () => {
    let copied = false;
    try { await navigator.clipboard.writeText(richShareUrl); copied = true; } catch {}
    if (!copied) {
      try {
        const ta = document.createElement("textarea");
        ta.value = richShareUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        copied = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {}
    }
    if (copied) toast.success("Lien copié 📋");
    else toast.error("Impossible de copier le lien");
  };

  const shareLink = async () => {
    const res = await shareOrCopy({ title: `Ma boutique ${user?.name} sur Fripri`, text: shareText, url: richShareUrl });
    if (res === "copied") toast.success("Lien copié ! Colle-le où tu veux 📋");
    else if (res === "failed") toast.error("Impossible de copier le lien");
  };

  return (
    <div className="border-2 border-black p-5 lg:p-8 space-y-5 bg-white" data-testid="shop-editor">
      <div>
        <h2 className="text-lg font-black tracking-tight">Ma vitrine PRO 🏪</h2>
        <p className="text-sm text-[color:var(--color-text-muted)] mt-1">Personnalisez votre boutique — visible par tous les visiteurs de votre profil.</p>
      </div>
      <div>
        <label className="label font-bold mb-2 block">Bannière de boutique</label>
        {form.shop_banner ? (
          <div className="relative border-2 border-black overflow-hidden mb-2">
            <img src={fileUrl(form.shop_banner)} className="w-full h-32 lg:h-44 object-cover" alt="Bannière" data-testid="shop-banner-preview" />
            <button type="button" onClick={() => setForm(f => ({ ...f, shop_banner: "" }))} className="absolute top-2 right-2 w-7 h-7 bg-white border-2 border-black flex items-center justify-center font-black" data-testid="remove-banner-btn">×</button>
          </div>
        ) : null}
        <label className="btn btn-ghost btn-sm cursor-pointer inline-flex" data-testid="upload-banner-btn">
          <ImagePlus size={14} strokeWidth={2.5} /> {uploading ? "Upload…" : form.shop_banner ? "Changer la bannière" : "Ajouter une bannière"}
          <input type="file" accept="image/*" className="hidden" onChange={uploadBanner} disabled={uploading} />
        </label>
      </div>
      <div>
        <label className="label font-bold mb-2 block">Accroche de la boutique</label>
        <input className="field" maxLength={80} placeholder="Ex : Le meilleur du vintage 90s, sélectionné avec amour" value={form.shop_headline} onChange={e=>setForm({...form, shop_headline:e.target.value})} data-testid="shop-headline-input" />
      </div>
      <div>
        <label className="label font-bold mb-2 block">Description de la boutique</label>
        <textarea rows="4" className="field" maxLength={600} placeholder="Racontez votre univers, vos marques fétiches, vos engagements…" value={form.shop_description} onChange={e=>setForm({...form, shop_description:e.target.value})} data-testid="shop-description-input" />
      </div>
      <div>
        <label className="label font-bold mb-2 block">Lien de partage de la boutique</label>
        <div className="flex items-stretch border-2 border-black overflow-hidden">
          <span className="flex items-center px-3 bg-[color:var(--color-surface)] text-sm font-bold border-r-2 border-black whitespace-nowrap">fripri.com/boutique/</span>
          <input className="flex-1 px-3 py-2 outline-none text-sm font-bold min-w-0" placeholder="ma-boutique" value={form.shop_slug}
            onChange={e => setForm({ ...form, shop_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="shop-slug-input" />
        </div>
      </div>
      <button type="button" onClick={save} disabled={saving || uploading} className="btn btn-mint w-full" data-testid="shop-save-btn">
        {saving ? "Enregistrement…" : "Enregistrer ma vitrine"}
      </button>
      {shopUrl && (
        <div className="border-2 border-black bg-[color:var(--color-yellow)] p-4 space-y-3" data-testid="shop-share-block">
          <p className="font-black tracking-tight text-sm">Partagez votre boutique 📣</p>
          <p className="text-sm break-all font-bold" data-testid="shop-share-url">{shopUrl}</p>
          <p className="text-xs">Le lien partagé affiche automatiquement votre bannière, votre accroche et votre badge PRO ✓ sur WhatsApp, Messages et les réseaux.</p>
          <div className="flex gap-2">
            <button type="button" onClick={copyLink} className="btn btn-ghost btn-sm flex-1" data-testid="shop-copy-btn"><Copy size={14}/> Copier</button>
            <button type="button" onClick={shareLink} className="btn btn-sm flex-1 bg-black text-white border-black" data-testid="shop-share-btn"><Share2 size={14}/> Partager</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShareStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/pro/share-stats").then(r => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return null;
  return (
    <div className="border-2 border-black p-5 lg:p-8 bg-white" data-testid="share-stats">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-[color:var(--color-mint)] border-2 border-black flex items-center justify-center"><BarChart3 size={16} strokeWidth={2.5}/></div>
        <h2 className="text-lg font-black tracking-tight">Stats de partage 📈</h2>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="border-2 border-black p-4 text-center" data-testid="share-stats-shop">
          <p className="text-3xl font-black">{stats.shop_clicks}</p>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--color-text-muted)] mt-1">Visites via ta boutique partagée</p>
        </div>
        <div className="border-2 border-black p-4 text-center" data-testid="share-stats-products">
          <p className="text-3xl font-black">{stats.product_clicks_total}</p>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--color-text-muted)] mt-1">Clics sur tes articles partagés</p>
        </div>
      </div>
      {stats.top_products?.length > 0 ? (
        <div>
          <p className="label font-bold mb-2">Tes articles les plus partagés</p>
          <div className="space-y-2">
            {stats.top_products.map(p => (
              <div key={p.product_id} className="flex items-center justify-between text-sm border-2 border-[color:var(--color-border-soft)] px-3 py-2">
                <span className="font-bold truncate">{p.title}</span>
                <span className="pill pill-mint text-xs shrink-0 ml-2">{p.share_clicks} clic{p.share_clicks > 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-[color:var(--color-text-muted)]">Partage tes articles et ta boutique pour voir combien de visiteurs tes liens ramènent ✨</p>
      )}
    </div>
  );
}

export default function FripriPro() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ activity: "vide_dressing", estimated_volume: "moins_500", company_name: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) api.get("/pro/my-application").then(r => setState(r.data)).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/pro/apply", form);
      toast.success("Demande envoyée 🎉 Nous revenons vers vous très vite !");
      const { data } = await api.get("/pro/my-application");
      setState(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSubmitting(false); }
  };

  const application = state?.application;
  const isPro = state?.is_pro;

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-6 lg:py-12 fade-in has-bottom-nav" data-testid="pro-page">
      <p className="label mb-2">Fripri Pro</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4">
        Passez à la<br/>vitesse supérieure.
      </h1>
      <p className="text-base md:text-lg text-[color:var(--color-text-muted)] max-w-xl mb-8">
        Vous vendez régulièrement ? Devenez vendeur professionnel — et on vous offre
        même la création de votre entreprise.
      </p>

      {/* Offre phare */}
      <div className="border-2 border-black bg-[color:var(--color-mint)] p-5 lg:p-8 mb-10" data-testid="pro-hero-offer">
        <p className="label font-bold mb-2">L'offre qui change tout 🎁</p>
        <h2 className="text-lg font-black tracking-tight mb-2">Votre entreprise créée GRATUITEMENT</h2>
        <p className="text-sm leading-relaxed max-w-2xl">
          Statut, immatriculation, formalités : on s'occupe de tout, en collaboration avec
          un cabinet formaliste partenaire. Vous vendez, ils gèrent la paperasse — sans frais.
        </p>
      </div>

      {/* Avantages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
        {PERKS.map((p, i) => (
          <div key={i} className="border-2 border-black p-4 flex gap-3 card-hover bg-white" data-testid={`pro-perk-${i}`}>
            <div className="w-10 h-10 shrink-0 border-2 border-black bg-[color:var(--color-yellow)] flex items-center justify-center">
              <p.icon size={18} strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="font-black tracking-tight">{p.title}</h3>
              <p className="text-sm text-[color:var(--color-text-muted)] mt-1">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Statut / Formulaire */}
      {!user ? (
        <div className="text-center border-2 border-dashed border-black py-12" data-testid="pro-login-cta">
          <p className="text-2xl font-black mb-4">Prêt à vous lancer ?</p>
          <Link to="/login" className="btn btn-mint">Se connecter pour candidater</Link>
        </div>
      ) : isPro ? (
        <div className="space-y-8">
          <div className="border-2 border-black bg-black text-white p-6 flex items-center gap-4" data-testid="pro-active-badge">
            <BadgeCheck size={32} className="text-[color:var(--color-mint)]" />
            <div>
              <p className="font-black text-lg">Vous êtes vendeur PRO ✓</p>
              <p className="text-sm text-white/70">Tous les avantages sont actifs sur votre compte.</p>
            </div>
          </div>
          <ShopEditor user={user} />
          <ShareStats />
        </div>
      ) : application?.status === "pending" ? (
        <div className="border-2 border-black bg-[color:var(--color-yellow)] p-6 flex items-center gap-4" data-testid="pro-pending">
          <CheckCircle2 size={28} strokeWidth={2.5} />
          <div>
            <p className="font-black">Demande envoyée ✓</p>
            <p className="text-sm">Notre équipe l'examine — vous recevrez une notification très vite.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="border-2 border-black p-5 lg:p-8 space-y-5 bg-white" data-testid="pro-apply-form">
          <h2 className="text-lg font-black tracking-tight">Demander mon passage en Pro</h2>
          {application?.status === "rejected" && (
            <p className="text-sm border-2 border-black bg-[color:var(--color-surface)] p-3">Votre précédente demande n'a pas abouti — vous pouvez en soumettre une nouvelle.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label font-bold mb-2 block">Votre activité</label>
              <select className="field" value={form.activity} onChange={e=>setForm({...form, activity:e.target.value})} data-testid="pro-activity-select">
                {ACTIVITIES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label font-bold mb-2 block">Volume de ventes estimé</label>
              <select className="field" value={form.estimated_volume} onChange={e=>setForm({...form, estimated_volume:e.target.value})} data-testid="pro-volume-select">
                {VOLUMES.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label font-bold mb-2 block">Entreprise existante (optionnel)</label>
              <input className="field" placeholder="Nom / SIRET si déjà créée" value={form.company_name} onChange={e=>setForm({...form, company_name:e.target.value})} data-testid="pro-company-input" />
            </div>
            <div>
              <label className="label font-bold mb-2 block">Téléphone (optionnel)</label>
              <input className="field" placeholder="06 12 34 56 78" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} data-testid="pro-phone-input" />
            </div>
          </div>
          <div>
            <label className="label font-bold mb-2 block">Parlez-nous de votre projet (optionnel)</label>
            <textarea rows="3" className="field" placeholder="Ce que vous vendez, vos objectifs…" value={form.message} onChange={e=>setForm({...form, message:e.target.value})} data-testid="pro-message-input" />
          </div>
          <button className="btn btn-mint w-full" type="submit" disabled={submitting} data-testid="pro-submit-btn">
            {submitting ? "Envoi…" : "Envoyer ma demande 💼"}
          </button>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Sans engagement. La création d'entreprise offerte est réalisée avec un cabinet formaliste partenaire après validation de votre demande.
          </p>
        </form>
      )}
    </div>
  );
}
